import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MapApiService } from '../../map/services/map-api.service';
import { ActivatedRoute, Router } from '@angular/router';
import Env from '@ridango/core/env';
import {
    ApplicationFacade,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    OtpUtil,
    RoutePatternStops,
    ShapeType,
    Stop
} from '@ridango/components';
import { RouteDirectionListModalComponent } from '../route-direction-list-modal/route-direction-list-modal';
import { ModalController } from '@ionic/angular';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-route-patterns',
    templateUrl: './route-patterns.component.html',
    styleUrls: ['./route-patterns.component.scss']
})
export class RoutePatternsComponent extends BaseComponent implements OnInit, OnDestroy {
    assetsPath$ = this.applicationFacade.assetsPath$;
    isLoading = true;
    data: RoutePatternStops[] = [];
    patternLabels: string[] = [];
    selectedOption: RoutePatternStops;
    isHistoryBack = this.env.EXPLORE?.SHOW_ROUTE_GROUPS;
    ShapeType = ShapeType;

    constructor(
        private applicationFacade: ApplicationFacade,
        private route: ActivatedRoute,
        private otpService: OpenTripPlannerService,
        private router: Router,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private mapApi: MapApiService,
        private modalCtrl: ModalController,
        @Inject('ENV') public env: Env,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscribeToRouteId();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.openTripPlannerFacade.clearExploreMapRoute();
    }

    navigateBack() {
        this.isHistoryBack ? history.back() : this.router.navigate([this.translate.currentLang, 'explore']);
    }

    handleStopSelect(stop: Stop) {
        this.router.navigate([`pattern/${this.selectedOption.pattern.id}/schedule/stop/${stop.id}`], {
            relativeTo: this.route.parent,
            state: { selectedOption: this.selectedOption, data: this.data }
        });
    }

    selectOption(selectedOptionId: string) {
        const selectedOption = this.data.find((option) => option.pattern.id === selectedOptionId);
        this.handlePatternSelect(selectedOption);
    }

    handlePatternSelect(option: RoutePatternStops) {
        this.selectedOption = option;
        this.setRouteOnMap();
    }

    private subscribeToRouteId() {
        this.route.paramMap.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            const routeId = params.get('routeId');

            this.isLoading = true;
            this.otpService
                .getRoutePatternStops(routeId, false)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (options) => {
                        this.data = options;
                        const preferHeadsign = this.env.EXPLORE?.PATTERN_LABEL_PREFER_HEADSIGN === true;
                        this.patternLabels = options.map((o) =>
                            OtpUtil.getPatternDisplayLabel(o.pattern, preferHeadsign)
                        );
                        if (options?.length > 0) {
                            this.selectedOption = options[0];
                            this.setRouteOnMap();
                        }
                    },
                    (error) => {
                        this.isLoading = true;
                        console.log(error, 'error');
                    }
                )
                .add(() => (this.isLoading = false));
        });
    }

    private setRouteOnMap() {
        this.openTripPlannerFacade.clearExploreMapRoute();

        this.otpService
            .getPatternGeometry(this.selectedOption.pattern.id)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((geometry) => {
                this.openTripPlannerFacade.setExploreMapRoute({
                    polyline: geometry.points,
                    polylineColor: this.selectedOption.route.color,
                    stops: OtpUtil.transformStopsStopMarkers(this.selectedOption.stops)
                });
            });
    }

    async openModal() {
        const modal = await this.modalCtrl.create({
            component: RouteDirectionListModalComponent,
            cssClass: 'route-direction-list-modal',
            componentProps: {
                routePatternStops: this.data,
                selectedOption: this.selectedOption
            }
        });

        await modal.present();

        modal.onWillDismiss().then(async (data) => {
            if (data.data) {
                this.selectedOption = data.data;
                this.setRouteOnMap();
            }
        });
    }
}
